// The truck and driver questions of a load (Load truck, Ship by job): Shipping hub, Phases 2-4, 2026-10-28.
//   Truck   GET /shipping/vehicles (Mike's list; may be empty) + "Other (type it)" (+ "No truck" when allowed)
//   Driver  an active person (GET /users) or "Someone else (type it)": a carrier, the customer's driver
// Values are plain objects the caller keeps: truck {choice: '' | 'v:<id>' | 'other' | 'none', text},
// driver {choice: '' | 'u:<id>' | 'other', text}; truckFields / driverFields turn them into the API's load fields.
import React, { useEffect, useState } from 'react';
import { loadDrivers, loadVehicles } from '../signing/signRequestsApi';

export const EMPTY_PICK = Object.freeze({ choice: '', text: '' });

const styles = {
  select: { minHeight: 48, fontSize: '1.05rem' },
  input: { minHeight: 48, fontSize: '1.05rem' },
};

/** {vehicles, loaded}: read once per mount; a failed read is an empty list. */
export function useVehicles(apiBase) {
  const [state, setState] = useState({ vehicles: [], loaded: false });
  useEffect(() => {
    let alive = true;
    loadVehicles(apiBase).then((r) => { if (alive) setState({ vehicles: r.vehicles, loaded: true }); });
    return () => { alive = false; };
  }, [apiBase]);
  return state;
}

/** {drivers, loaded}. */
export function useDrivers(apiBase) {
  const [state, setState] = useState({ drivers: [], loaded: false });
  useEffect(() => {
    let alive = true;
    loadDrivers(apiBase).then((drivers) => { if (alive) setState({ drivers, loaded: true }); });
    return () => { alive = false; };
  }, [apiBase]);
  return state;
}

/** truck value -> {vehicle_id, vehicle_text}; null when nothing usable was picked. 'none' -> both null. */
export function truckFields(truck) {
  const t = truck || EMPTY_PICK;
  if (t.choice === 'none') return { vehicle_id: null, vehicle_text: null };
  if (t.choice && t.choice.startsWith('v:')) return { vehicle_id: Number(t.choice.slice(2)), vehicle_text: null };
  if (t.choice === 'other' && String(t.text || '').trim()) return { vehicle_id: null, vehicle_text: String(t.text).trim() };
  return null;
}

/** driver value -> {driver_user_id, driver_name}; null when nothing usable was picked. */
export function driverFields(driver) {
  const d = driver || EMPTY_PICK;
  if (d.choice && d.choice.startsWith('u:')) return { driver_user_id: Number(d.choice.slice(2)), driver_name: null };
  if (d.choice === 'other' && String(d.text || '').trim()) return { driver_user_id: null, driver_name: String(d.text).trim() };
  return null;
}

/** The words for a picked truck / driver ("Truck 2", "R+L Carriers"). */
export function truckName(truck, vehicles) {
  const t = truck || EMPTY_PICK;
  if (t.choice && t.choice.startsWith('v:')) {
    const v = (vehicles || []).find((x) => String(x.vehicle_id) === t.choice.slice(2));
    return v ? v.name : '';
  }
  if (t.choice === 'other') return String(t.text || '').trim();
  return '';
}

export function driverName(driver, drivers) {
  const d = driver || EMPTY_PICK;
  if (d.choice && d.choice.startsWith('u:')) {
    const u = (drivers || []).find((x) => String(x.user_id) === d.choice.slice(2));
    return u ? u.name : '';
  }
  if (d.choice === 'other') return String(d.text || '').trim();
  return '';
}

export function TruckPicker({ id, label = 'Truck / trailer', vehicles, value, onChange, allowNone = false, noneLabel = 'No truck', required = false }) {
  const v = value || EMPTY_PICK;
  return (
    <div>
      <label className="form-label fw-semibold" htmlFor={id}>{label}{required ? ' *' : ''}</label>
      <select id={id} className="form-select" style={styles.select} value={v.choice}
        onChange={(e) => onChange({ choice: e.target.value, text: e.target.value === 'other' ? v.text : '' })}>
        <option value="">Pick a truck…</option>
        {(vehicles || []).map((x) => (
          <option key={x.vehicle_id} value={`v:${x.vehicle_id}`}>{x.name}{x.kind ? ` · ${x.kind}` : ''}</option>
        ))}
        <option value="other">Other (type it)</option>
        {allowNone && <option value="none">{noneLabel}</option>}
      </select>
      {v.choice === 'other' && (
        <input type="text" className="form-control mt-2" style={styles.input} aria-label="Which truck (typed)"
          placeholder="Which truck or trailer" value={v.text} maxLength={120}
          onChange={(e) => onChange({ choice: 'other', text: e.target.value })} />
      )}
    </div>
  );
}

export function DriverPicker({ id, label = 'Driver', drivers, value, onChange, otherLabel = 'Someone else (type it)', required = false }) {
  const v = value || EMPTY_PICK;
  return (
    <div>
      <label className="form-label fw-semibold" htmlFor={id}>{label}{required ? ' *' : ''}</label>
      <select id={id} className="form-select" style={styles.select} value={v.choice}
        onChange={(e) => onChange({ choice: e.target.value, text: e.target.value === 'other' ? v.text : '' })}>
        <option value="">Pick the driver…</option>
        {(drivers || []).map((u) => <option key={u.user_id} value={`u:${u.user_id}`}>{u.name}</option>)}
        <option value="other">{otherLabel}</option>
      </select>
      {v.choice === 'other' && (
        <input type="text" className="form-control mt-2" style={styles.input} aria-label="Driver or carrier (typed)"
          placeholder="Driver's name or carrier" value={v.text} maxLength={120}
          onChange={(e) => onChange({ choice: 'other', text: e.target.value })} />
      )}
    </div>
  );
}
